/**
 * Whether this tab has seen a server action its deployment no longer has
 * (`"deploy"`), or an action answer that was not an action answer at all,
 * e.g. a 5xx or an HTML error page (`"unexpected-response"`). Only the first
 * means a new release; the banner words the second without claiming one.
 */

type Listener = () => void;

export type StaleReason = "deploy" | "unexpected-response";

let pageStale = false;
let staleReason: StaleReason | null = null;
let handledInPlacePath: string | null = null;
const listeners = new Set<Listener>();

function notify() {
  for (const listener of listeners) listener();
}

export function isPageStale(): boolean {
  return pageStale;
}

export function pageStaleReason(): StaleReason | null {
  return staleReason;
}

export function staleHandledInPlacePath(): string | null {
  return handledInPlacePath;
}

/**
 * The app-wide banner, unless the screen at `pathname` already shows its
 * own reload path. Scoped to that pathname so a client navigation elsewhere
 * brings the banner back.
 */
export function isStaleBannerVisible(pathname: string): boolean {
  return pageStale && handledInPlacePath !== pathname;
}

export function subscribePageStale(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function markPageStale(reason: StaleReason = "deploy"): void {
  // A known missing action outranks an unexplained failure; never downgrade.
  if (pageStale && (staleReason === "deploy" || reason === staleReason)) return;
  pageStale = true;
  staleReason = reason;
  notify();
}

/**
 * For screens with a better recovery than a plain reload (the order wizard
 * resumes its saved draft), so the user is not offered two different
 * Reload buttons.
 */
export function markStaleHandledInPlace(pathname: string): void {
  if (pageStale && handledInPlacePath === pathname) return;
  pageStale = true;
  staleReason = "deploy";
  handledInPlacePath = pathname;
  notify();
}

export function resetPageStaleForTests(): void {
  pageStale = false;
  staleReason = null;
  handledInPlacePath = null;
}
